import { isNewEvidence } from '@utils/survey/evidence';
import { HandleNewEvidenceFn, EvidenceFile } from '@g17eco/types/file';
import { AiProcessingValue } from '@g17eco/types/ai';
import { Toggle } from '@g17eco/molecules/toggle';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { isAddedToLibrary } from '@utils/files';
import { QUESTION } from '@constants/terminology';

const METRIC_EXCLUDED_TOOLTIP = `This ${QUESTION.SINGULAR} is excluded from AI processing. Evidence uploaded here cannot be AI processed.`;

interface AIProcessingToggleProps {
  file: EvidenceFile;
  handleAiProcessing: HandleNewEvidenceFn | undefined;
  disabled?: boolean;
}

export const AIProcessingToggle = (props: AIProcessingToggleProps) => {
  const { file, handleAiProcessing, disabled = false } = props;

  const isExisting = !isNewEvidence(file);
  const isExcludedFromAiProcessing = isExisting ? false : (file.excludedFromAiProcessing ?? false);
  const isInLibrary = isExisting && isAddedToLibrary(file);

  // Don't render for existing files that were never in library
  if (isExisting && !isInLibrary) {
    return null;
  }

  // Don't render for new evidence without handler
  if (!isExisting && !handleAiProcessing) {
    return null;
  }

  const isSavedToLibrary = isExisting ? isInLibrary : (file.saveToLibrary ?? false);

  const isAIProcessingEnabled = file.aiProcessing === AiProcessingValue.Include || file.aiProcessing === undefined;

  const getTooltipText = () => {
    if (isExcludedFromAiProcessing) {
      return METRIC_EXCLUDED_TOOLTIP;
    }
    if (isExisting && isInLibrary) {
      return 'Document is now in Document Library. To change AI settings, contact your administrator.';
    }
    return undefined;
  };

  return (
    <SimpleTooltip text={getTooltipText()}>
      <div className='d-flex gap-1 mt-2 align-items-center' data-testid='ai-processing-toggle'>
        <Toggle
          checked={!isExcludedFromAiProcessing && isAIProcessingEnabled && isSavedToLibrary}
          disabled={isExcludedFromAiProcessing || isExisting || disabled || !isSavedToLibrary}
          onChange={() => {
            if (!isExisting && handleAiProcessing) {
              handleAiProcessing(file);
            }
          }}
          testId='ai-processing-switch'
        />
        <span className={!isSavedToLibrary ? 'text-muted' : ''}>AI processing</span>
      </div>
    </SimpleTooltip>
  );
};
